/**
 * The freshness decision behind `KissSoundSubscriber` — the emote-kind twin of
 * `../dice/feedFreshness.ts`'s `selectFreshRolls`.
 *
 * **The bug this avoids:** `useGameSession`'s `feed` starts `[]` and only fills in
 * asynchronously when `game_welcome` delivers the backfill — so judging "already seen" off the
 * subscriber's own first observation would record an empty baseline on mount, then read the
 * *entire* backfilled history as new the moment it lands, playing the kiss sound for every
 * historical `/kiss` on every reload or join.
 *
 * **The fix:** freshness is judged against a caller-supplied `highWater` timestamp seeded from
 * the welcome backfill itself (`GameSession.feedBaselineTs`, re-seeded on every `game_welcome`
 * including a mid-session reconnect), not from anything observed client-side at mount. An entry
 * is fresh iff its server-stamped `ts` is strictly greater than `highWater`. Because `ts` is
 * non-decreasing in feed order and server-stamped (no client clock skew to reason about),
 * `ts > highWater` is both the freshness test AND — combined with raising `highWater` to the
 * feed's max `ts` after every call — the de-dup mechanism.
 */
import type { EmoteFeedEntry, FeedEntry } from "@lancer-vtt/shared";

/** The result of one freshness pass: the emote entries to sound + the new high-water mark. */
export type FreshEmotesResult = {
  /** `kind: "emote"` entries with `ts > highWater`, in feed order. */
  emotes: EmoteFeedEntry[];
  /** `max(highWater, ...feed.map(e => e.ts))` — monotonically non-decreasing across calls. */
  nextHighWater: number;
};

/**
 * Select the emote entries in `feed` newer than `highWater` (strictly — a `ts` equal to the
 * high-water mark is already-seen, not fresh), and compute the next high-water mark. Pure and
 * total: an empty feed returns no emotes and leaves `highWater` unchanged.
 */
export function selectFreshEmotes(feed: readonly FeedEntry[], highWater: number): FreshEmotesResult {
  const emotes: EmoteFeedEntry[] = [];
  let nextHighWater = highWater;
  for (const entry of feed) {
    if (entry.kind === "emote" && entry.ts > highWater) emotes.push(entry);
    if (entry.ts > nextHighWater) nextHighWater = entry.ts;
  }
  return { emotes, nextHighWater };
}
